/** Share a place: a link that opens the receiver's map where the sender was.
 *
 * The link is the app's own URL plus a hash —
 *
 *   #map=11.50/33.31520/44.36610/30/45&pin=33.31520,44.36610&e=<id>&story=<id>
 *
 * `map` is zoom/lat/lon[/bearing[/pitch]] (MapLibre's own hash order), `pin`
 * a spot the sender pointed at, `e` an object whose card opens, `story` the
 * scope the sender was looking through (`all` for All stories). A HASH, never
 * a query string: it never reaches the server, so it can't land in an access
 * log, and it survives the login screen — the app re-renders in place, so a
 * receiver who has to sign in first still lands on the shared view.
 *
 * Nothing in a link widens what the receiver may see. An object they can't
 * read 404s like any other, and a story they can't see is refused by name
 * and their own scope kept.
 *
 * Two doors send: right-click the map (coordinates, this spot, this view, the
 * object under the pointer) and the top strip's ⇪, which on a phone — where
 * there is no right-click — is the whole feature.
 */
import * as maplibregl from 'maplibre-gl';
import { bestClickHit, CLICK_PRIORITY, cachedEntity } from './objects';
import { toast } from './hud';

export interface ShareView {
  zoom: number;
  lat: number;
  lon: number;
  bearing: number;
  pitch: number;
  /** [lat, lon] of a spot the sender pointed at. */
  pin: [number, number] | null;
  entity: string | null;
  /** A story id, `null` for All stories, `undefined` when the link says nothing. */
  story?: string | null;
}

const ID_RE = /^[\w-]{1,64}$/;
const round = (n: number, dp: number): string => n.toFixed(dp).replace(/\.?0+$/, '') || '0';

/** Latitude/longitude as people paste them: `lat, lon`, 5 dp (~1 m). */
export function formatLatLon(lat: number, lon: number): string {
  return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
}

/** Longitude folded into [-180, 180] — a globe spun past the antimeridian
 *  reports 200° and more. */
function wrapLon(lon: number): number {
  return ((((lon + 180) % 360) + 360) % 360) - 180;
}

export function encodeShare(v: ShareView): string {
  const parts = [round(v.zoom, 2), v.lat.toFixed(5), wrapLon(v.lon).toFixed(5)];
  const bearing = Math.round(v.bearing);
  const pitch = Math.round(v.pitch);
  if (bearing || pitch) parts.push(String(bearing));
  if (pitch) parts.push(String(pitch));
  let out = `map=${parts.join('/')}`;
  if (v.pin) out += `&pin=${v.pin[0].toFixed(5)},${wrapLon(v.pin[1]).toFixed(5)}`;
  if (v.entity && ID_RE.test(v.entity)) out += `&e=${v.entity}`;
  if (v.story !== undefined) {
    out += `&story=${v.story && ID_RE.test(v.story) ? v.story : 'all'}`;
  }
  return out;
}

function latLon(lat: number, lon: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lon)
    && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;
}

/** The share in a location hash, or null when there isn't one. Forgiving of
 *  what a chat client does to a link (a trailing dot, an encoded `/`), strict
 *  about what it believes: a garbled `map` with a good `pin` still centres on
 *  the pin, and nothing out of range is ever applied. */
export function parseShare(hash: string): ShareView | null {
  const params = new URLSearchParams(hash.replace(/^#/, '').replace(/[.)\]]+$/, ''));
  if (!params.has('map') && !params.has('pin') && !params.has('e')) return null;

  let pin: [number, number] | null = null;
  const p = (params.get('pin') ?? '').split(',').map(Number);
  const [plat = NaN, plon = NaN] = p;
  if (p.length === 2 && latLon(plat, plon)) pin = [plat, plon];

  const [zoom = NaN, lat = NaN, lon = NaN, bearing = 0, pitch = 0] =
    (params.get('map') ?? '').split('/').map(Number);
  let view: Pick<ShareView, 'zoom' | 'lat' | 'lon' | 'bearing' | 'pitch'> | null = null;
  if (Number.isFinite(zoom) && zoom >= 0 && zoom <= 22 && latLon(lat, lon)) {
    view = {
      zoom, lat, lon,
      bearing: Number.isFinite(bearing) ? bearing : 0,
      pitch: Number.isFinite(pitch) ? Math.max(0, Math.min(85, pitch)) : 0,
    };
  } else if (pin) {
    view = { zoom: 13, lat: pin[0], lon: pin[1], bearing: 0, pitch: 0 };
  }

  const e = params.get('e');
  const entity = e && ID_RE.test(e) ? e : null;
  if (!view && !entity) return null;

  const s = params.get('story');
  const story = s === null ? undefined
    : s === 'all' ? null
    : ID_RE.test(s) ? s : undefined;

  return {
    ...(view ?? { zoom: NaN, lat: NaN, lon: NaN, bearing: 0, pitch: 0 }),
    pin, entity, story,
  };
}

/** The URL for a share, on whatever origin the sender is on. */
export function shareUrl(v: ShareView): string {
  return `${location.origin}${location.pathname}#${encodeShare(v)}`;
}

/** Clipboard, falling back to the old execCommand path (an http:// dev host
 *  has no async clipboard), and last to a prompt the user copies from — a
 *  share that silently copied nothing would be sent as whatever was on the
 *  clipboard before. */
export async function copyText(text: string, done: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast(done);
    return;
  } catch { /* fall through */ }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try { ok = document.execCommand('copy'); } catch { ok = false; }
  ta.remove();
  if (ok) toast(done);
  else window.prompt('Copy this:', text);
}

// --- runtime ----------------------------------------------------------------

export interface ShareHooks {
  /** Open an object's card. */
  openEntity: (id: string) => void;
  /** The object whose card is open, if any — the ⇪ link carries it. */
  selectedEntityId: () => string | null;
  /** Current scope: a story id, or null for All stories. */
  currentStory: () => string | null;
  /** Switch scope; false when this user can't see that story. */
  applyStory: (id: string | null) => boolean;
  /** True while a tool owns right-click (a boundary edit deletes vertices). */
  blocked: () => boolean;
}

let pinMarker: maplibregl.Marker | null = null;
let offHashChange: (() => void) | null = null;

function viewOf(map: maplibregl.Map, hooks: ShareHooks,
                extra: Partial<ShareView> = {}): ShareView {
  const c = map.getCenter();
  return {
    zoom: map.getZoom(), lat: c.lat, lon: c.lng,
    bearing: map.getBearing(), pitch: map.getPitch(),
    pin: null, entity: null, story: hooks.currentStory(),
    ...extra,
  };
}

/** The ⇪ button: this view, plus the object whose card is open. */
export function copyViewLink(map: maplibregl.Map, hooks: ShareHooks): void {
  const entity = hooks.selectedEntityId();
  void copyText(shareUrl(viewOf(map, hooks, { entity })),
    entity ? 'Link to this view and the open card copied.' : 'Link to this view copied.');
}

function dropPin(map: maplibregl.Map, lat: number, lon: number): void {
  pinMarker?.remove();
  const box = document.createElement('div');
  box.className = 'share-pop';
  box.innerHTML = `<b>Shared location</b><span></span>
    <div><button type="button" data-a="copy">Copy</button>
    <button type="button" data-a="remove">Remove pin</button></div>`;
  box.querySelector('span')!.textContent = formatLatLon(lat, lon);
  const marker = new maplibregl.Marker({ color: '#e5484d' })
    .setLngLat([lon, lat])
    .setPopup(new maplibregl.Popup({ offset: 30, closeButton: false }).setDOMContent(box))
    .addTo(map);
  box.addEventListener('click', (ev) => {
    const a = (ev.target as HTMLElement).dataset.a;
    if (a === 'copy') void copyText(formatLatLon(lat, lon), 'Coordinates copied.');
    if (a === 'remove') { marker.remove(); if (pinMarker === marker) pinMarker = null; }
  });
  marker.togglePopup();
  pinMarker = marker;
}

/** Apply a share to the map. `fly` for a link pasted into an open tab — the
 *  receiver sees where they went; a fresh load just starts there. */
function applyShare(map: maplibregl.Map, v: ShareView, hooks: ShareHooks,
                    fly: boolean): void {
  if (v.story !== undefined && v.story !== hooks.currentStory()) {
    if (!hooks.applyStory(v.story)) {
      toast('The link was shared from a story you can’t see — keeping your current scope.');
    }
  }
  if (Number.isFinite(v.zoom)) {
    const cam = { center: [v.lon, v.lat] as [number, number], zoom: v.zoom,
                  bearing: v.bearing, pitch: v.pitch };
    if (fly) map.flyTo(cam); else map.jumpTo(cam);
  }
  if (v.pin) dropPin(map, v.pin[0], v.pin[1]);
  if (v.entity) hooks.openEntity(v.entity);
}

/** Read a share out of the address bar, apply it, and clear it — so a reload
 *  doesn't yank the map back and the address bar isn't mistaken for a link
 *  to wherever the user has since panned. */
function consumeHash(map: maplibregl.Map, hooks: ShareHooks, fly: boolean): void {
  const v = parseShare(location.hash);
  if (!v) return;
  history.replaceState(null, '', location.pathname + location.search);
  applyShare(map, v, hooks, fly);
}

/** Right-click menu, the ⇪ button's wiring is main.ts's. Call once the
 *  object layers exist (the entity half of a link opens a card). */
export function installShare(map: maplibregl.Map, hooks: ShareHooks): void {
  consumeHash(map, hooks, false);

  // A link pasted into the address bar of a tab already running the app
  // changes only the hash: no reload, so listen for it.
  offHashChange?.();
  const onHash = () => consumeHash(map, hooks, true);
  window.addEventListener('hashchange', onHash);
  offHashChange = () => window.removeEventListener('hashchange', onHash);

  // A right-DRAG rotates the map and ends in a contextmenu event too; only a
  // right-click that stayed put opens the menu.
  let downAt: { x: number; y: number } | null = null;
  map.getCanvas().addEventListener('mousedown', (ev) => {
    if (ev.button === 2) downAt = { x: ev.clientX, y: ev.clientY };
  });

  let menu: HTMLElement | null = null;
  const close = () => {
    menu?.remove();
    menu = null;
    document.removeEventListener('pointerdown', onOutside, true);
    document.removeEventListener('keydown', onKey, true);
    map.off('movestart', close);
  };
  const onOutside = (ev: Event) => {
    if (menu && !menu.contains(ev.target as Node)) close();
  };
  const onKey = (ev: KeyboardEvent) => {
    if (ev.key === 'Escape' && menu) { ev.stopPropagation(); close(); }
  };

  map.on('contextmenu', (e) => {
    const oe = e.originalEvent as MouseEvent;
    const moved = downAt && Math.hypot(oe.clientX - downAt.x, oe.clientY - downAt.y) > 5;
    downAt = null;
    if (moved || hooks.blocked()) return;
    oe.preventDefault();
    close();

    const { lat, lng: lon } = e.lngLat.wrap();
    const hit = bestClickHit(map.queryRenderedFeatures(e.point, { layers: [...CLICK_PRIORITY] })
      .filter((f) => f.properties?.id && f.layer.id !== 'control-fill'));
    const hitId = hit?.properties?.id as string | undefined;
    const hitName = hitId
      ? String(cachedEntity(hitId)?.name ?? hit?.properties?.name ?? 'this object')
      : null;

    menu = document.createElement('div');
    menu.className = 'share-menu';
    menu.innerHTML = `<div class="share-menu-head"></div>
      <button type="button" data-a="coords">Copy coordinates</button>
      <button type="button" data-a="spot">Copy link to this spot</button>
      <button type="button" data-a="view">Copy link to this view</button>
      ${hitId ? '<button type="button" data-a="entity"></button>' : ''}`;
    menu.querySelector('.share-menu-head')!.textContent = formatLatLon(lat, lon);
    const eb = menu.querySelector<HTMLElement>('[data-a="entity"]');
    if (eb) eb.textContent = `Copy link to ${hitName}`;
    menu.addEventListener('click', (ev) => {
      const a = (ev.target as HTMLElement).dataset.a;
      if (!a) return;
      close();
      if (a === 'coords') {
        void copyText(formatLatLon(lat, lon), 'Coordinates copied.');
      } else if (a === 'spot') {
        void copyText(shareUrl(viewOf(map, hooks, { pin: [lat, lon] })),
                      'Link to this spot copied — it opens with a pin here.');
      } else if (a === 'view') {
        void copyText(shareUrl(viewOf(map, hooks)), 'Link to this view copied.');
      } else if (a === 'entity' && hitId) {
        void copyText(shareUrl(viewOf(map, hooks, { entity: hitId })),
                      `Link to ${hitName} copied.`);
      }
    });

    // Placed at the pointer, kept on screen.
    const host = map.getContainer();
    host.appendChild(menu);
    const w = menu.offsetWidth, h = menu.offsetHeight;
    menu.style.left = `${Math.max(4, Math.min(e.point.x, host.clientWidth - w - 4))}px`;
    menu.style.top = `${Math.max(4, Math.min(e.point.y, host.clientHeight - h - 4))}px`;

    document.addEventListener('pointerdown', onOutside, true);
    document.addEventListener('keydown', onKey, true);
    map.on('movestart', close);
  });
}
